import { gql } from '@apollo/client';
import _ from 'lodash';
import React, { useState } from 'react';
import { When } from 'react-if';

import {
  CreditFactorTradeline,
  CreditFactorTradelineSection,
} from '@generated-gql/generated-types';
import classes from '@nerdwallet/base-styles/classes';
import { Link } from '@nerdwallet/currency/components/Link';

import TradelineSection from '../tradeline-section';

const MAX_TRADELINES_TO_SHOW = 6;

interface Component<E> extends React.FC<E> {
  fragment: any;
}

interface Props {
  sections: CreditFactorTradelineSection[];
  TradelineListItem: React.FC<{ tradeline: CreditFactorTradeline }>;
}

const TradelineSections: Component<Props> = ({
  sections = [],
  TradelineListItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    setIsOpen((currentValue) => !currentValue);
  };

  /**
   * Assume there are a maximum of 2 sections:
   * - max 1 "priority" section
   * - max 1 "non-priority" section
   */
  const prioritySection = _.find(sections, (section) => section.isPriority);

  const numPriorityTradelines = prioritySection
    ? prioritySection.tradelines.length
    : 0;
  const nonPrioritySections = _.filter(
    sections,
    (section) => !section.isPriority,
  );
  const numNonPriorityTradelines = nonPrioritySections.reduce(
    (acc, section) => acc + section.tradelines.length,
    0,
  );
  const numNonPriorityTradelinesToShow = Math.max(
    0,
    MAX_TRADELINES_TO_SHOW - numPriorityTradelines,
  );

  return (
    <ul className={classes('width-100')}>
      {sections.map((section) => (
        <TradelineSection
          key={`tradeline-section-${section.title}`}
          maxTradelinesToShow={
            isOpen || section.isPriority ? null : numNonPriorityTradelinesToShow
          }
          section={section}
          TradelineListItem={TradelineListItem}
        />
      ))}

      <When
        condition={numNonPriorityTradelines > numNonPriorityTradelinesToShow}
      >
        <Link onClick={toggleOpen}>{isOpen ? 'See fewer' : 'See more'}</Link>
      </When>
    </ul>
  );
};
TradelineSections.fragment = gql`
  fragment tradelineSectionsFragment on CreditFactorTradelineSection {
    isPriority
    title
    ...tradelineSectionFragment
  }
  ${TradelineSection.fragment}
`;

export default TradelineSections;
